import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
    getAllUsers,
    getInstitutions,
    getDepartments,
    getTerms,
    getDepartmentTermCapacities,
    getFixedAssignments,
    createFixedAssignment,
    deleteFixedAssignment,
} from '../services/api';

const AdminFixedAssignmentManager = () => {
    const [users, setUsers] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [terms, setTerms] = useState([]);
    const [dtcs, setDtcs] = useState([]); // DepartmentTermCapacities
    const [fixedAssignments, setFixedAssignments] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Form State
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedInstitutionId, setSelectedInstitutionId] = useState('');
    const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
    const [selectedTermId, setSelectedTermId] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [
                usersData,
                instData,
                deptData,
                termsData,
                dtcData,
                assignData
            ] = await Promise.all([
                getAllUsers(),
                getInstitutions(),
                getDepartments(),
                getTerms(),
                getDepartmentTermCapacities(),
                getFixedAssignments()
            ]);

            setUsers(usersData);
            setInstitutions(instData);
            setDepartments(deptData);
            setTerms(termsData);
            setDtcs(dtcData);
            setFixedAssignments(assignData);
        } catch (err) {
            console.error('Failed to fetch data:', err);
            setError('データの取得に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!selectedUserId || !selectedInstitutionId || !selectedDepartmentId || !selectedTermId) {
            alert('すべての項目を選択してください。');
            return;
        }

        // Find DTC ID based on selection
        const targetDtc = dtcs.find(d =>
            d.institution_id === parseInt(selectedInstitutionId) &&
            d.department_id === parseInt(selectedDepartmentId) &&
            d.term_id === parseInt(selectedTermId)
        );

        if (!targetDtc) {
            alert('選択された組み合わせの枠（DepartmentTermCapacity）が存在しません。');
            return;
        }

        setSubmitting(true);
        setSubmitMessage('');
        try {
            await createFixedAssignment({
                user_id: selectedUserId,
                department_term_capacity_id: targetDtc.id
            });
            setSubmitMessage('固定割り当てを作成しました。');
            // Refresh list
            const newAssignments = await getFixedAssignments();
            setFixedAssignments(newAssignments);
            // Reset form (optional)
            setSelectedUserId('');
        } catch (err) {
            console.error('Failed to create fixed assignment:', err);
            if (err.response && err.response.data && err.response.data.detail) {
                alert(`エラー: ${err.response.data.detail}`);
            } else {
                alert('作成に失敗しました。');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('本当に削除しますか？')) return;
        try {
            await deleteFixedAssignment(id);
            setFixedAssignments(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            console.error('Failed to delete assignment:', err);
            alert('削除に失敗しました。');
        }
    };

    // 選択された病院に基づいて診療科をフィルタリング（DTCに存在する組み合わせのみ）
    const availableDepartments = useMemo(() => {
        if (!selectedInstitutionId) return [];
        const validDeptIds = new Set(
            dtcs.filter(d => d.institution_id === parseInt(selectedInstitutionId)).map(d => d.department_id)
        );
        return departments.filter(d => validDeptIds.has(d.id));
    }, [selectedInstitutionId, dtcs, departments]);

    // 選択された病院・診療科に基づいてタームをフィルタリング
    const availableTerms = useMemo(() => {
        if (!selectedInstitutionId || !selectedDepartmentId) return [];
        const validTermIds = new Set(
            dtcs
                .filter(d =>
                    d.institution_id === parseInt(selectedInstitutionId) &&
                    d.department_id === parseInt(selectedDepartmentId)
                )
                .map(d => d.term_id)
        );
        return terms.filter(t => validTermIds.has(t.id));
    }, [selectedInstitutionId, selectedDepartmentId, dtcs, terms]);

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900">
            <header className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link
                            to="/admin-dashboard"
                            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
                            aria-label="ダッシュボードに戻る"
                        >
                            <ArrowLeft size={24} />
                        </Link>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Admin Console</p>
                            <h1 className="text-2xl font-bold text-gray-900">固定割り当て管理 (pre_fixed)</h1>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
                {/* 新規作成フォーム */}
                <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Plus className="w-5 h-5 text-blue-600" />
                        新規固定割り当て作成
                    </h2>
                    {submitMessage && (
                        <div className="mb-4 p-3 bg-emerald-50 text-emerald-700 rounded-md border border-emerald-200 text-sm font-medium">
                            {submitMessage}
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">ユーザー</label>
                            <select
                                value={selectedUserId}
                                onChange={e => setSelectedUserId(e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            >
                                <option value="">選択してください</option>
                                {[...users].sort((a, b) => a.username.localeCompare(b.username)).map(user => (
                                    <option key={user.id} value={user.auth_user_id}>
                                        {user.username} ({user.email})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">病院</label>
                            <select
                                value={selectedInstitutionId}
                                onChange={e => {
                                    setSelectedInstitutionId(e.target.value);
                                    setSelectedDepartmentId('');
                                    setSelectedTermId('');
                                }}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            >
                                <option value="">選択してください</option>
                                {institutions.map(inst => (
                                    <option key={inst.id} value={inst.id}>{inst.short_name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">診療科</label>
                            <select
                                value={selectedDepartmentId}
                                onChange={e => {
                                    setSelectedDepartmentId(e.target.value);
                                    setSelectedTermId('');
                                }}
                                disabled={!selectedInstitutionId}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                <option value="">選択してください</option>
                                {availableDepartments.map(dept => (
                                    <option key={dept.id} value={dept.id}>{dept.short_name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">ターム</label>
                            <select
                                value={selectedTermId}
                                onChange={e => setSelectedTermId(e.target.value)}
                                disabled={!selectedDepartmentId}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                <option value="">選択してください</option>
                                {availableTerms.map(term => (
                                    <option key={term.id} value={term.id}>{term.term_name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button
                            onClick={handleCreate}
                            disabled={submitting || !selectedUserId || !selectedTermId}
                            className="bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300"
                        >
                            {submitting ? '作成中...' : '作成する'}
                        </Button>
                    </div>
                </section>

                {/* 一覧テーブル */}
                <section className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">固定割り当て一覧</h2>
                    </div>
                    {loading ? (
                        <div className="p-12 text-center text-gray-500">読み込み中...</div>
                    ) : error ? (
                        <div className="p-12 text-center text-red-600">{error}</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader className="bg-gray-50">
                                    <TableRow>
                                        <TableHead className="font-bold">ユーザー</TableHead>
                                        <TableHead className="font-bold">病院</TableHead>
                                        <TableHead className="font-bold">診療科</TableHead>
                                        <TableHead className="font-bold">ターム</TableHead>
                                        <TableHead className="font-bold w-[100px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {fixedAssignments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                                登録データはありません
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        fixedAssignments.map(assign => (
                                            <TableRow key={assign.id} className="hover:bg-gray-50">
                                                <TableCell className="font-medium text-gray-900">{assign.username}</TableCell>
                                                <TableCell>{assign.institution_name}</TableCell>
                                                <TableCell>{assign.department_name}</TableCell>
                                                <TableCell>{assign.term_name}</TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleDelete(assign.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default AdminFixedAssignmentManager;
